import { Component, Inject, OnInit } from '@angular/core';
import {
    FormArray,
    FormBuilder,
    FormControl,
    FormGroup,
    Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
    ConsultasOrdenService,
    TipoOrdenConsulta,
    TipoOrdenVentana,
} from '@services/orden-consultas/signo-orden-consultas.service';
import { SignoSedeService } from '@services/entidades/signo-sedes.service';
import { Observable, of } from 'rxjs';
import { map, startWith } from 'rxjs/operators';

type Mode = 'create' | 'edit';
interface SedeMin {
    _id: string;
    nombre: string;
}

@Component({
    selector: 'app-tipo-orden-form-dialog',
    templateUrl: './tipo-orden-form-dialog.component.html',
    styleUrls: ['./tipo-orden-form-dialog.component.scss'],
})
export class TipoOrdenFormDialogComponent implements OnInit {
    mode: Mode = 'create';
    form!: FormGroup;
    guardando = false;

    allSedes: SedeMin[] = [];
    filteredSedes$!: Observable<SedeMin[]>;

    dayOptions = [
        { label: 'Dom', value: 0 },
        { label: 'Lun', value: 1 },
        { label: 'Mar', value: 2 },
        { label: 'Mié', value: 3 },
        { label: 'Jue', value: 4 },
        { label: 'Vie', value: 5 },
        { label: 'Sáb', value: 6 },
    ];

    constructor(
        private fb: FormBuilder,
        private dialogRef: MatDialogRef<TipoOrdenFormDialogComponent>,
        @Inject(MAT_DIALOG_DATA)
        public data: { mode: Mode; item: TipoOrdenConsulta | null },
        private srv: ConsultasOrdenService,
        private sedesSrv: SignoSedeService
    ) {}

    // ⬇️ Hacemos async para poder esperar las sedes
    async ngOnInit(): Promise<void> {
        this.mode = this.data.mode;

        this.form = this.fb.group({
            nombre: ['', [Validators.required, Validators.maxLength(120)]],
            descripcion: [''],

            sedes: new FormArray([] as FormControl[]), // IDs
            sedeInput: [null], // objeto seleccionado o texto

            ventanas: new FormArray([] as FormGroup[]),

            maxPerMonth: [null],
            maxTotal: [null],
            caducidadHoras: [null],
            activo: [true],
        });

        // 1) cargar sedes
        await this.loadSedes();

        // 2) rehidratar si es edición
        if (this.mode === 'edit' && this.data.item) {
            const it = this.data.item;
            this.form.patchValue({
                nombre: it.nombre,
                descripcion: it.descripcion || '',
                maxPerMonth: it.maxPerMonth ?? null,
                maxTotal: it.maxTotal ?? null,
                caducidadHoras: it.caducidadHoras ?? null,
                activo: it.activo,
            });
            (it.sedes || []).forEach((sId) => this.addSedeId(sId));
            (it.ventanas || []).forEach((v) => this.addVentana(v));
        } else {
            this.addVentana({
                days: [1, 2, 3, 4, 5],
                start: '15:00',
                end: '16:00',
            });
            this.addVentana({ days: [0, 6], start: '18:00', end: '08:00' });
        }
    }

    // ======= getters =======
    get sedesFA(): FormArray {
        return this.form.get('sedes') as FormArray;
    }
    get ventanasFA(): FormArray {
        return this.form.get('ventanas') as FormArray;
    }

    // ======= SEDES =======
    private async loadSedes(): Promise<void> {
        try {
            const list: any[] = await this.sedesSrv.getSedes(); // <-- usa tu Promise
            this.allSedes = (list || []).map((s) => ({
                _id: String(s._id),
                nombre: String(s.nombre || s.name || '—'),
            }));

            // preparar filtro del autocomplete
            this.filteredSedes$ = this.form.get('sedeInput')!.valueChanges.pipe(
                startWith(this.form.get('sedeInput')!.value ?? ''),
                map((value) =>
                    typeof value === 'string' ? value : value?.nombre ?? ''
                ),
                map((text) => this.filterSedes(text))
            );
        } catch (e) {
            console.error('Error cargando sedes', e);
            this.allSedes = [];
            this.filteredSedes$ = of([]);
        }
    }

    displaySede = (s?: SedeMin | null): string => (s ? s.nombre : '');

    private filterSedes(text: string): SedeMin[] {
        if (!text) return this.allSedes;
        const q = text.toLowerCase().trim();
        return this.allSedes.filter((s) => s.nombre.toLowerCase().includes(q));
    }

    private addSedeId(id: string): void {
        const trimmed = (id || '').trim();
        if (!trimmed) return;
        if (
            this.sedesFA.controls.some(
                (c) => (c as FormControl).value === trimmed
            )
        )
            return;
        this.sedesFA.push(new FormControl(trimmed));
    }

    onSedeSelected(s: SedeMin): void {
        if (s?._id) this.addSedeId(s._id);
        this.form.get('sedeInput')!.reset(null);
    }

    addSelectedSede(): void {
        const val = this.form.get('sedeInput')!.value;
        if (typeof val === 'string') {
            const match = this.allSedes.find(
                (s) =>
                    s.nombre.toLowerCase() === val.toLowerCase() ||
                    s._id === val
            );
            if (match) this.addSedeId(match._id);
        } else if (val && val._id) {
            this.addSedeId(val._id);
        }
        this.form.get('sedeInput')!.reset(null);
    }

    removeSede(i: number): void {
        this.sedesFA.removeAt(i);
    }

    sedeName(id: string): string {
        return this.allSedes.find((s) => s._id === id)?.nombre || id;
    }

    // ======= VENTANAS / SAVE (igual que antes, con normalización HH:mm) =======
    private toHHmm(v: string): string {
        if (!v) return v;
        const [hRaw, mRaw = '00'] = v.split(':');
        const h = String(Number(hRaw)).padStart(2, '0');
        const m = String(Number(mRaw)).padStart(2, '0');
        return `${h}:${m}`;
    }

    addVentana(v?: Partial<TipoOrdenVentana>): void {
        this.ventanasFA.push(
            this.fb.group({
                days: [v?.days ?? [], [Validators.required]],
                start: [v?.start ?? '', [Validators.required]],
                end: [v?.end ?? '', [Validators.required]],
            })
        );
    }
    removeVentana(i: number): void {
        this.ventanasFA.removeAt(i);
    }

    toggleDay(iVent: number, day: number): void {
        const fg = this.ventanasFA.at(iVent) as FormGroup;
        const cur: number[] = [...((fg.get('days')?.value as number[]) || [])];
        const idx = cur.indexOf(day);
        if (idx >= 0) cur.splice(idx, 1);
        else cur.push(day);
        fg.get('days')?.setValue(cur.sort((a, b) => a - b));
    }

    save(): void {
        if (this.form.invalid) {
            this.form.markAllAsTouched();
            return;
        }
        this.guardando = true;

        const sedes: string[] = this.sedesFA.controls
            .map((c) => String((c as FormControl).value || '').trim())
            .filter(Boolean);

        const ventanas = this.ventanasFA.controls.map((fg) => {
            const v: any = (fg as FormGroup).value;
            const days = (v.days || [])
                .map((d: any) => Number(d))
                .filter((n: number) => !Number.isNaN(n));
            return {
                days,
                start: this.toHHmm(v.start),
                end: this.toHHmm(v.end),
            };
        });

        const dto: any = {
            nombre: this.form.get('nombre')!.value,
            descripcion: this.form.get('descripcion')!.value || undefined,
            sedes: sedes.length ? sedes : undefined,
            ventanas: ventanas.length ? ventanas : undefined,
            maxPerMonth: this.form.get('maxPerMonth')!.value ?? undefined,
            maxTotal: this.form.get('maxTotal')!.value ?? undefined,
            caducidadHoras: this.form.get('caducidadHoras')!.value ?? undefined,
            activo: !!this.form.get('activo')!.value,
        };

        const obs =
            this.mode === 'create'
                ? this.srv.createTipoOrden$(dto)
                : this.srv.updateTipoOrden$(this.data.item!._id!, dto);

        obs.subscribe({
            next: () => {
                this.guardando = false;
                this.dialogRef.close(true);
            },
            error: (e) => {
                console.error(e);
                this.guardando = false;
            },
        });
    }

    cancel(): void {
        this.dialogRef.close(false);
    }
}
